/*
 * Layout-level locale plumbing, loaded with `defer` from layout/theme.liquid.
 * It used to be an inline <script> at the end of <body> on every page; as an
 * asset it is cached across navigations instead of re-sent and re-compiled
 * with each document. Three independent pieces:
 *
 *  1. Locale link guard — internal links written without a language prefix
 *     would land on the primary locale and bounce through a client redirect
 *     (a blank double-load). Rewrite them at click time to this page's
 *     language root on this origin (exposed by snippets/locale-middleware).
 *     When that root is '/', bare links already land on this language and
 *     are left alone. Links that declare their own language (hreflang — the
 *     language switcher) are never rewritten.
 *  2. ms_lang cookie — the middleware only honours ms_lang, it no longer
 *     writes it on passive renders: an explicit switcher choice records the
 *     preference, and it must land before the navigation it triggers.
 *  3. The language / country dropdowns and the localization loading overlay.
 *
 * The page language comes from <html lang>, which the layout sets from the
 * same resolved value it used to interpolate here.
 */
(() => {
  const ROOTS = window.__msLocaleRoots || {};
  const root = ROOTS[document.documentElement.lang] || '/';
  if (root === '/') return;
  const SKIP = /^\/(ar|en|fr)(\/|$)|^\/(cdn|admin|apps|services|tools|wpm|payments|checkout)(\/|$)/;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('hreflang')) return;
    const href = link.getAttribute('href');
    if (!href || href[0] !== '/' || href.startsWith('//') || SKIP.test(href)) return;
    link.setAttribute('href', root + href);
  }, true);
})();
(() => {
  const remember = (event) => {
    const option = event.target.closest('[data-ms-lang-code]');
    if (!option) return;
    /*
     * Liquid cannot see the query string, so the switcher hrefs carry
     * the path only — a search's ?q= would be dropped on switch. Graft
     * the current params on at click time, rebuilding from the path so
     * a bfcache-restored page never keeps a stale set.
     */
    const href = option.getAttribute('href');
    if (href && href[0] === '/' && href[1] !== '/') {
      const path = href.split('?')[0];
      if (path !== href || location.search) {
        option.setAttribute('href', path + location.search);
      }
    }
    const code = option.getAttribute('data-ms-lang-code');
    if (code !== 'ar' && code !== 'en') return;
    document.cookie = 'ms_lang=' + code + '; path=/; max-age=31536000; SameSite=Lax';
  };
  document.addEventListener('click', remember, true);
  document.addEventListener('auxclick', remember, true);
})();
(() => {
  const closeDropdown = (root) => {
    if (!root) return;
    root.classList.remove('is-open');
    const menu = root.querySelector('[data-ms-lang-menu], [data-ms-country-menu]');
    const button = root.querySelector('[data-ms-lang-toggle], [data-ms-country-toggle]');
    if (menu) menu.hidden = true;
    if (button) button.setAttribute('aria-expanded', 'false');
  };

  const closeAll = (except = null) => {
    document.querySelectorAll('[data-ms-lang], [data-ms-country]').forEach((el) => {
      if (el !== except) closeDropdown(el);
    });
  };

  const stopLoading = () => {
    document.documentElement.classList.remove('ms-localization-loading');
    const overlay = document.querySelector('[data-ms-localization-overlay]');
    if (overlay) overlay.setAttribute('aria-busy', 'false');
    document.querySelectorAll('[data-ms-country-option][disabled], [data-ms-lang] button[disabled], [data-ms-country-toggle][disabled]').forEach((el) => {
      el.removeAttribute('disabled');
    });
  };

  const startLoading = () => {
    document.documentElement.classList.add('ms-localization-loading');
    const overlay = document.querySelector('[data-ms-localization-overlay]');
    if (overlay) overlay.setAttribute('aria-busy', 'true');
    document.querySelectorAll('[data-ms-country-option], [data-ms-lang] button, [data-ms-country-toggle]').forEach((el) => {
      el.setAttribute('disabled', 'disabled');
    });
    window.setTimeout(stopLoading, 15000);
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) stopLoading();
  });

  document.addEventListener('click', (event) => {
    if (document.documentElement.classList.contains('ms-localization-loading')) {
      event.preventDefault();
      return;
    }

    const langToggle = event.target.closest('[data-ms-lang-toggle]');
    const langRoot = event.target.closest('[data-ms-lang]');
    const countryToggle = event.target.closest('[data-ms-country-toggle]');
    const countryRoot = event.target.closest('[data-ms-country]');
    const countryOption = event.target.closest('[data-ms-country-option]');

    if (countryOption) {
      event.preventDefault();
      const root = countryOption.closest('[data-ms-country]');
      const form = countryOption.closest('form');
      const current = root?.getAttribute('data-current-country');
      const next = countryOption.getAttribute('data-country-code');
      closeAll();
      if (!form || !next || next === current) return;
      let countryInput = form.querySelector('input[name="country_code"]');
      if (!countryInput) {
        countryInput = document.createElement('input');
        countryInput.type = 'hidden';
        countryInput.name = 'country_code';
        form.appendChild(countryInput);
      }
      countryInput.value = next;
      startLoading();
      form.submit();
      return;
    }

    if (langToggle && langRoot) {
      const willOpen = !langRoot.classList.contains('is-open');
      closeAll(willOpen ? langRoot : null);
      if (willOpen) {
        langRoot.classList.add('is-open');
        const menu = langRoot.querySelector('[data-ms-lang-menu]');
        if (menu) menu.hidden = false;
        langToggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (countryToggle && countryRoot) {
      const willOpen = !countryRoot.classList.contains('is-open');
      closeAll(willOpen ? countryRoot : null);
      if (willOpen) {
        countryRoot.classList.add('is-open');
        const menu = countryRoot.querySelector('[data-ms-country-menu]');
        if (menu) menu.hidden = false;
        countryToggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (!langRoot && !countryRoot) closeAll();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('ms-country-form') &&
        !form.classList.contains('ms-mobile-country')) {
      return;
    }
    closeAll();
    startLoading();
  });

  document.addEventListener('click', (event) => {
    const langOption = event.target.closest('[data-ms-lang-option], .ms-drawer__lang-link');
    if (!langOption) return;
    if (langOption.classList.contains('is-active')) {
      event.preventDefault();
      return;
    }
    const code = langOption.getAttribute('data-ms-lang-code');
    if (code === 'ar' || code === 'en') {
      document.cookie = 'ms_lang=' + encodeURIComponent(code) + '; path=/; max-age=31536000; SameSite=Lax';
    }
    startLoading();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });
})();
